import React from 'react';
import styled, { css } from 'styled-components';
import { focusRingSibling } from '@pleo-io/telescope-focus-ring';
import { Text } from '@pleo-io/telescope-text';
import { spacing2, fastInOut, spacing16, arc4, circle, shade000, shade800, shade500, shade400, shade700, shade200 } from '@pleo-io/telescope-tokens';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var variants = function variants(variantsConfig) {
  return function (_ref) {
    var variant = _ref.variant;

    if (!variant) {
      return null;
    }

    return variantsConfig[variant];
  };
};

function _templateObject10() {
  var data = _taggedTemplateLiteralLoose(["\n                        background-color: ", ";\n\n                        ", " {\n                            stroke: ", ";\n                        }\n                    "]);

  _templateObject10 = function _templateObject10() {
    return data;
  };

  return data;
}

function _templateObject9() {
  var data = _taggedTemplateLiteralLoose(["\n            flex: 0 0 19px;\n            width: 19px;\n            height: 19px;\n            background-color: ", ";\n            border: 1px solid ", ";\n            border-radius: ", ";\n\n            &:hover {\n                ", "\n            }\n        "]);

  _templateObject9 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8() {
  var data = _taggedTemplateLiteralLoose(["\n            flex: 0 0 16px;\n            width: 16px;\n            height: 16px;\n            background-color: ", ";\n            border: 2px solid ", ";\n            border-radius: ", ";\n        "]);

  _templateObject8 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7() {
  var data = _taggedTemplateLiteralLoose(["\n            margin-right: ", ";\n        "]);

  _templateObject7 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6() {
  var data = _taggedTemplateLiteralLoose(["\n    margin-top: ", ";\n    transition: ", ";\n    display: inline-flex;\n    justify-content: center;\n    align-items: center;\n    ", "\n\n    ", "\n\n    ", "\n"]);

  _templateObject6 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5() {
  var data = _taggedTemplateLiteralLoose(["\n    position: absolute;\n    width: 1px;\n    height: 1px;\n    margin: -1px;\n    padding: 0;\n    overflow: hidden;\n    white-space: nowrap;\n    border: none;\n    clip: rect(0 0 0 0);\n    clip-path: inset(50%);\n"]);

  _templateObject5 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4() {
  var data = _taggedTemplateLiteralLoose(["\n            width: 60%;\n            stroke: ", ";\n            stroke-width: 5px;\n        "]);

  _templateObject4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n            width: 100%;\n            stroke: ", ";\n            stroke-width: 3px;\n        "]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n    visibility: ", ";\n    fill: none;\n\n    ", "\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n    display: inline-flex;\n    flex-direction: row;\n    justify-content: center;\n    cursor: ", ";\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * Checkbox - based on the native input type="checkbox"
 */


var Checkbox = function Checkbox(_ref) {
  var className = _ref.className,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'square' : _ref$variant,
      checked = _ref.checked,
      disabled = _ref.disabled,
      children = _ref.children,
      onClick = _ref.onClick,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? function () {} : _ref$onChange,
      props = _objectWithoutPropertiesLoose(_ref, ["className", "variant", "checked", "disabled", "children", "onClick", "onChange"]);

  return React.createElement(CheckboxContainer, {
    className: className,
    onClick: onClick,
    checked: checked,
    disabled: disabled
  }, React.createElement(HiddenCheckbox, Object.assign({
    checked: checked,
    disabled: disabled,
    onChange: onChange
  }, props)), React.createElement(StyledCheckbox, {
    variant: variant,
    disabled: disabled,
    checked: !!checked,
    hasLabel: !!children,
    "data-telescope-checkbox": true
  }, React.createElement(Icon, {
    variant: variant,
    disabled: disabled,
    checked: checked,
    viewBox: "0 0 24 24",
    "data-telescope-checkbox-icon": true
  }, React.createElement("polyline", {
    points: "20 6 9 17 4 12"
  }))), children);
};

var CheckboxContainer = /*#__PURE__*/styled(Text).attrs({
  as: 'label'
})( /*#__PURE__*/_templateObject(), function (_ref2) {
  var disabled = _ref2.disabled;
  return disabled ? 'not-allowed' : 'pointer';
});
var Icon = /*#__PURE__*/styled.svg( /*#__PURE__*/_templateObject2(), function (props) {
  return props.checked ? 'visible' : 'hidden';
}, /*#__PURE__*/variants({
  square: /*#__PURE__*/css( /*#__PURE__*/_templateObject3(), function (_ref3) {
    var disabled = _ref3.disabled;
    return disabled ? shade500 : shade800;
  }),
  round: /*#__PURE__*/css( /*#__PURE__*/_templateObject4(), shade000)
}));
var HiddenCheckbox = /*#__PURE__*/styled.input.withConfig({
  shouldForwardProp: function shouldForwardProp(prop, defaultValidatorFn) {
    return !['form'].includes(prop) && defaultValidatorFn(prop);
  }
}).attrs({
  type: 'checkbox'
})( /*#__PURE__*/_templateObject5());

var borderColorSquare = function borderColorSquare(_ref4) {
  var disabled = _ref4.disabled,
      checked = _ref4.checked;

  if (disabled) {
    return shade400;
  }

  return checked ? shade800 : shade500;
};

var borderColorRound = function borderColorRound(_ref5) {
  var checked = _ref5.checked;
  return checked ? shade800 : shade700;
};

var backgroundSquare = function backgroundSquare(_ref6) {
  var disabled = _ref6.disabled;
  return disabled ? shade200 : shade000;
};

var backgroundRound = function backgroundRound(_ref7) {
  var checked = _ref7.checked,
      disabled = _ref7.disabled;

  if (checked) {
    return shade800;
  }

  return disabled ? shade200 : shade000;
};

var StyledCheckbox = /*#__PURE__*/styled.div.withConfig({
  shouldForwardProp: function shouldForwardProp(prop, defaultValidatorFn) {
    return !['checked'].includes(prop) && defaultValidatorFn(prop);
  }
})( /*#__PURE__*/_templateObject6(), spacing2, fastInOut, /*#__PURE__*/focusRingSibling(HiddenCheckbox), function (_ref8) {
  var hasLabel = _ref8.hasLabel;
  return hasLabel && css(_templateObject7(), spacing16);
}, /*#__PURE__*/variants({
  square: /*#__PURE__*/css( /*#__PURE__*/_templateObject8(), backgroundSquare, borderColorSquare, arc4),
  round: /*#__PURE__*/css( /*#__PURE__*/_templateObject9(), backgroundRound, borderColorRound, circle, function (_ref9) {
    var checked = _ref9.checked,
        disabled = _ref9.disabled;
    return checked && !disabled && css(_templateObject10(), shade000, Icon, shade800);
  })
}));
export { Checkbox };